import { useState } from "react";
import { Loader2, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useCreateIntegration, useTestIntegration } from "@/hooks/use-api";
import { useQueryClient } from "@tanstack/react-query";
import type { IntegrationSourceType } from "@/types/api";

interface FieldConfig {
  key: string;
  label: string;
  placeholder: string;
  type?: "text" | "password" | "json";
  /**
   * For `json` fields: keys that must be present in the parsed object for the
   * value to be considered valid (e.g. the fields of a GCP service-account key).
   */
  requiredJsonKeys?: string[];
}

interface IntegrationConfig {
  sourceType: IntegrationSourceType;
  title: string;
  description: string;
  fields: FieldConfig[];
}

export const INTEGRATION_CONFIGS: Record<string, IntegrationConfig> = {
  entra_id: {
    sourceType: "entra_id",
    title: "Microsoft Entra ID",
    description:
      "Connect your Microsoft Entra ID (Azure AD) tenant to monitor identity and sign-in events.",
    fields: [
      {
        key: "tenant_id",
        label: "Tenant ID",
        placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
      },
      {
        key: "client_id",
        label: "Client ID",
        placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
      },
      {
        key: "client_secret",
        label: "Client Secret",
        placeholder: "Enter client secret",
        type: "password",
      },
    ],
  },
  cloudtrail: {
    sourceType: "cloudtrail",
    title: "AWS CloudTrail",
    description:
      "Connect your AWS CloudTrail to monitor API activity and configuration changes.",
    fields: [
      {
        key: "aws_account_id",
        label: "AWS Account ID",
        placeholder: "123456789012",
      },
      {
        key: "region",
        label: "Region",
        placeholder: "us-east-1",
      },
    ],
  },
  m365: {
    sourceType: "m365",
    title: "Microsoft 365",
    description:
      "Connect Microsoft 365 to monitor email activity, Teams, and Office audit logs.",
    fields: [
      {
        key: "tenant_id",
        label: "Tenant ID",
        placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
      },
      {
        key: "client_id",
        label: "Client ID",
        placeholder: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx",
      },
      {
        key: "client_secret",
        label: "Client Secret",
        placeholder: "Enter client secret",
        type: "password",
      },
    ],
  },
  gcp_audit: {
    sourceType: "gcp_audit",
    title: "GCP Audit Logs",
    description:
      "Connect Google Cloud Audit Logs to monitor IAM changes, service-account key creation, and resource activity across your project.",
    fields: [
      {
        key: "project_id",
        label: "Project ID",
        placeholder: "my-gcp-project-123456",
      },
      {
        key: "service_account_json",
        label: "Service Account Key (JSON)",
        placeholder:
          '{\n  "type": "service_account",\n  "project_id": "...",\n  "private_key": "-----BEGIN PRIVATE KEY-----\\n...",\n  "client_email": "reader@project.iam.gserviceaccount.com"\n}',
        type: "json",
        requiredJsonKeys: ["type", "private_key", "client_email", "project_id"],
      },
    ],
  },
  firewall: {
    sourceType: "firewall",
    title: "Firewall",
    description:
      "Connect your firewall to monitor network traffic and security events.",
    fields: [
      {
        key: "vendor",
        label: "Vendor",
        placeholder: "e.g. palo_alto, fortinet, sonicwall",
      },
      {
        key: "source_ip",
        label: "Source IP",
        placeholder: "192.168.1.1",
      },
    ],
  },
};

interface IntegrationStepProps {
  stepKey: string;
  isCompleted: boolean;
  isOptional?: boolean;
}

export function IntegrationStep({
  stepKey,
  isCompleted,
  isOptional = false,
}: IntegrationStepProps) {
  const config = INTEGRATION_CONFIGS[stepKey];
  const qc = useQueryClient();

  const [formValues, setFormValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(config?.fields.map((f) => [f.key, ""]) ?? []),
  );
  const [testStatus, setTestStatus] = useState<"idle" | "success" | "failure">(
    "idle",
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const createIntegration = useCreateIntegration();
  const testIntegration = useTestIntegration();

  if (!config) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        Unknown integration type: {stepKey}
      </div>
    );
  }

  if (isCompleted) {
    return (
      <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
        <CardContent className="flex items-center gap-3 py-6">
          <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600 dark:text-green-400" />
          <div>
            <p className="font-medium text-green-800 dark:text-green-300">
              Connected and verified
            </p>
            <p className="text-sm text-green-700 dark:text-green-400">
              {config.title} is connected and working correctly.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isSubmitting =
    createIntegration.isPending || testIntegration.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTestStatus("idle");
    setErrorMessage(null);

    // Build the config payload. `json` fields (e.g. a GCP service-account key)
    // are parsed to an object so the stored value is the shape the backend
    // probe/poller expect; invalid JSON or a missing required key blocks submit
    // with an inline field error. text/password fields pass through as strings.
    const nextErrors: Record<string, string> = {};
    const fullConfig: Record<string, unknown> = {};
    for (const field of config.fields) {
      const raw = formValues[field.key] ?? "";
      if (field.type !== "json") {
        fullConfig[field.key] = raw;
        continue;
      }
      let parsed: unknown;
      try {
        parsed = JSON.parse(raw);
      } catch {
        nextErrors[field.key] =
          "Must be valid JSON — paste the full contents of the downloaded key file.";
        continue;
      }
      const obj =
        typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
          ? (parsed as Record<string, unknown>)
          : null;
      const missing = (field.requiredJsonKeys ?? []).filter((k) => !obj?.[k]);
      if (!obj || missing.length > 0) {
        nextErrors[field.key] = obj
          ? `Not a valid service account key (missing: ${missing.join(", ")}).`
          : "Must be a JSON object.";
        continue;
      }
      fullConfig[field.key] = obj;
    }
    if (Object.keys(nextErrors).length > 0) {
      setFieldErrors(nextErrors);
      return;
    }
    setFieldErrors({});

    let integrationId: string;
    try {
      const result = await createIntegration.mutateAsync({
        source_type: config.sourceType,
        display_name: config.title,
        config: fullConfig,
      });
      integrationId = result.integration.id;
    } catch {
      setTestStatus("failure");
      setErrorMessage("Failed to create integration. Check your credentials.");
      return;
    }

    try {
      const testResult = await testIntegration.mutateAsync(integrationId);
      if (testResult.status === "success" || testResult.status === "warning") {
        setTestStatus("success");
        qc.invalidateQueries({ queryKey: ["onboarding"] });
      } else {
        setTestStatus("failure");
        setErrorMessage("Integration test did not pass. Please check settings.");
      }
    } catch {
      setTestStatus("failure");
      setErrorMessage("Integration test failed. Unable to reach the service.");
    }
  };

  const handleRetry = () => {
    setTestStatus("idle");
    setErrorMessage(null);
    setFieldErrors({});
    createIntegration.reset();
    testIntegration.reset();
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">{config.title}</h3>
          {isOptional && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              Optional
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{config.description}</p>
      </div>

      {testStatus === "success" ? (
        <Card className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
          <CardContent className="flex items-center gap-3 py-6">
            <CheckCircle2 className="h-6 w-6 shrink-0 text-green-600 dark:text-green-400" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-300">
                Connected successfully
              </p>
              <p className="text-sm text-green-700 dark:text-green-400">
                {config.title} has been connected and verified.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {config.fields.map((field) => (
            <div key={field.key} className="space-y-1.5">
              <Label htmlFor={field.key}>{field.label}</Label>
              {field.type === "json" ? (
                <Textarea
                  id={field.key}
                  placeholder={field.placeholder}
                  value={formValues[field.key]}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                  disabled={isSubmitting}
                  required
                  rows={8}
                  spellCheck={false}
                  aria-invalid={Boolean(fieldErrors[field.key])}
                  className="font-mono text-xs"
                />
              ) : (
                <Input
                  id={field.key}
                  type={field.type ?? "text"}
                  placeholder={field.placeholder}
                  value={formValues[field.key]}
                  onChange={(e) =>
                    setFormValues((prev) => ({
                      ...prev,
                      [field.key]: e.target.value,
                    }))
                  }
                  disabled={isSubmitting}
                  required
                />
              )}
              {fieldErrors[field.key] && (
                <p
                  className="text-sm text-red-600 dark:text-red-400"
                  role="alert"
                >
                  {fieldErrors[field.key]}
                </p>
              )}
            </div>
          ))}

          {testStatus === "failure" && (
            <Card className="border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30">
              <CardContent className="flex items-start gap-3 py-4">
                <XCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 dark:text-red-300">
                    Connection failed
                  </p>
                  {errorMessage && (
                    <p className="mt-0.5 text-sm text-red-700 dark:text-red-400">
                      {errorMessage}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRetry}
                  className="shrink-0 text-red-700 hover:text-red-900 dark:text-red-400"
                >
                  <RotateCcw className="mr-1 h-3.5 w-3.5" />
                  Retry
                </Button>
              </CardContent>
            </Card>
          )}

          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {createIntegration.isPending ? "Connecting..." : "Testing..."}
              </>
            ) : (
              "Connect & Verify"
            )}
          </Button>
        </form>
      )}
    </div>
  );
}
